import React, { useEffect, useState, useCallback } from "react";
import { apiFetch } from "../../../../apiFetch";
import { BASE_URL } from "../../../../config";
import "./ManageContract.css";

const toAbsoluteUrl = (maybePathOrUrl) => {
    if (!maybePathOrUrl) return "#";
    if (/^https?:\/\//i.test(maybePathOrUrl)) return maybePathOrUrl;
    try {
        return new URL(maybePathOrUrl, BASE_URL).toString();
    } catch {
        return "#";
    }
};

const parseDuration = (durationStr) => {
    const lower = (durationStr || "").toLowerCase().trim();
    const number = parseInt(lower, 10);
    if (Number.isNaN(number)) return 0;

    if (lower.includes("year")) return number * 365 * 24 * 60 * 60 * 1000;
    if (lower.includes("month")) return number * 30 * 24 * 60 * 60 * 1000;
    if (lower.includes("day")) return number * 24 * 60 * 60 * 1000;
    if (lower.includes("hour")) return number * 60 * 60 * 1000;
    if (lower.includes("minute")) return number * 60 * 1000;
    return 0;
};

const formatTime = (ms) => {
    if (ms <= 0) return "Expired";
    const totalSeconds = Math.floor(ms / 1000);
    const days = Math.floor(totalSeconds / (60 * 60 * 24));
    const hours = Math.floor((totalSeconds / (60 * 60)) % 24);
    const minutes = Math.floor((totalSeconds / 60) % 60);
    const seconds = totalSeconds % 60;
    return `${days}d ${hours}h ${minutes}m ${seconds}s`;
};

// ✅ Payment helpers (MATCH PLAYER)
const isPaid = (contract) => {
    return (contract?.paymentStatus || "").toLowerCase() === "paid";
};


// ✅ TEMP HOLD marker (competing offers parked while approved offer awaits payment)
const TEMP_HOLD_PREFIX = "TEMP_HOLD_AWAITING_PAYMENT:";
const COMPETING_ACTIVATED_PREFIX = "CLOSED_OTHER_CONTRACT_ACTIVATED:";

const isTempHoldCompetingOffer = (contract) => {
    const rr = String(contract?.rejectionReason || "");
    return rr.startsWith(TEMP_HOLD_PREFIX);
};


const isClosedBecauseAnotherActivated = (contract) => {
    const rr = String(contract?.rejectionReason || "");
    return rr.startsWith(COMPETING_ACTIVATED_PREFIX);
};


// ✅ Closed because player rejected (player never signed) — but NOT TEMP HOLD
const isClosedByPlayerRejection = (contract) => {
    const status = String(contract?.status || "").toLowerCase();
    if (status !== "closed") return false;

    // Don't mislabel TEMP HOLD competing offers as "Rejected by Player"
    if (isTempHoldCompetingOffer(contract)) return false;

    return !contract?.signedByPlayer;
};



// ✅ Start time = activation time (payment success time)
// Prefer activatedAt. Keep safe fallbacks in case your backend uses a different field name.
const getActivationStartTimeMs = (contract) => {
    const t =
        contract?.activatedAt ||
        contract?.paidAt ||
        contract?.paymentPaidAt ||
        contract?.paymentCompletedAt ||
        contract?.paymentSuccessTime ||
        contract?.activationTime ||
        null;

    if (!t) return null;

    const ms = new Date(t).getTime();
    return Number.isNaN(ms) ? null : ms;
};

// ✅ NEW: Hold expiry timestamp (server should send activationHoldExpiresAt)
const getActivationHoldExpiresAtMs = (contract) => {
    const t =
        contract?.activationHoldExpiresAt ||
        contract?.holdExpiresAt ||
        contract?.activationHoldExpiry ||
        contract?.approvalHoldExpiresAt ||
        contract?.offerHoldExpiresAt ||
        contract?.pendingPaymentExpiresAt ||
        contract?.expiresAt ||
        null;

    if (!t) return null;

    const ms = new Date(t).getTime();
    return Number.isNaN(ms) ? null : ms;
};

// ✅ NEW: Hold expired? (only meaningful if unpaid)
const isActivationHoldExpired = (contract) => {
    if (!contract) return false;
    if (isPaid(contract)) return false;

    const expiresAtMs = getActivationHoldExpiresAtMs(contract);
    if (!expiresAtMs) return false; // if backend doesn't send it, we cannot safely declare expired in UI

    return Date.now() > expiresAtMs;
};

// ✅ Single source of truth: when is a contract ACTIVE? (MATCHES PLAYER ACTIVATION MODEL)
const isContractActive = (contract) => {
    if (!contract) return false;

    // Must be admin approved
    if ((contract.adminApproval || "").toLowerCase() !== "approved") return false;

    // Must be signed by player
    if (!contract.signedByPlayer) return false;

    // Must be activated/paid to become ACTIVE
    if (!isPaid(contract)) return false;

    // Explicitly block dead states (NO "expired" — expiry is computed)
    if (
        contract.status === "terminated" ||
        contract.status === "rejected" ||
        contract.status === "closed" ||
        contract.status === "withdrawn"
    ) {
        return false;
    }

    // Contract starts ONLY after activation/payment success
    const startTime = getActivationStartTimeMs(contract);
    if (!startTime) return false;

    const durationMs = parseDuration(contract.contractDuration);
    if (!durationMs || durationMs <= 0) return false;

    const endTime = startTime + durationMs;

    return Date.now() < endTime;
};

const ContractStatusStamp = ({ type, title, subtitle }) => {
    return (
        <div className={`status-stamp ${type}`}>
            <div className="status-stamp-subtitle">{subtitle}</div>
            <div className="status-stamp-title">{title}</div>
        </div>
    );
};

const getPlayerSlug = (contract) =>
    contract?.playerProfileSlug ||
    contract?.player?.profileSlug ||
    contract?.playerSlug ||
    contract?.profileSlug ||
    contract?.playerUsername || // optional fallback if username == slug in your system
    null;

const ManageContract = ({ role, userId, agentId, scoutId, clubId }) => {
    const normalizedRole = (role || "agent").toLowerCase();

    const partnerLabelByRole = {
        agent: "Agent Name",
        scout: "Scout Name",
        club: "Club Name",
    };

    const partnerNameByRole = {
        agent: (contract) => contract.agentName,
        scout: (contract) => contract.scoutName,
        club: (contract) => contract.clubName,
    };

    // Resolve a single effective id (from RoleDashboard context)
    // role-first effective id to avoid picking wrong identity when multiple ids exist
    const effectiveId = (() => {
        const roleLookup = {
            player: userId,
            agent: agentId || userId,
            scout: scoutId || userId,
            club: clubId || userId,
        };
        return roleLookup[normalizedRole] ?? userId ?? null;
    })();

    const [contracts, setContracts] = useState([]);
    const [terminationRequests, setTerminationRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [viewMode, setViewMode] = useState("contracts");
    const [timers, setTimers] = useState({});
    const [timersReady, setTimersReady] = useState(false);
    const [showReasonModal, setShowReasonModal] = useState(false);
    const [selectedContractId, setSelectedContractId] = useState(null);
    const [selectedPlayerId, setSelectedPlayerId] = useState(null);
    const [terminationReason, setTerminationReason] = useState("");
    const [cooldownMap, setCooldownMap] = useState({});
    const [submittingDecision, setSubmittingDecision] = useState(false);
    const [submittingRequest, setSubmittingRequest] = useState(false);

    // ✅ NEW: activation state (pay after approval)
    const [activatingMap, setActivatingMap] = useState({}); // { [contractId]: true/false }

    // ✅ UPDATED: Keep CLOSED contracts ONLY if they are unpaid (Activation Failed / hold expired / awaiting activation states)
    // Hide: rejected
    // Hide: closed+paid (those are truly dead/archived in this UI)
    // Keep: closed+unpaid (so Activation Failed can be shown)
    const visibleContracts = contracts.filter((contract) => {
        const status = (contract.status || "").toLowerCase();
        const paid = isPaid(contract);

        if (status === "rejected") return false;
        if (status === "closed" && paid) return false;

        return true;
    });

    // ✅ Count pending player termination requests for ANY active contract
    const pendingTerminationCount = terminationRequests.filter((req) => {
        if ((req.status || "").toLowerCase() !== "pending") return false;
        if ((req.requestedBy || "").toLowerCase() !== "player") return false;

        return contracts.some(
            (contract) => contract.id === req.contractId && isContractActive(contract)
        );
    }).length;

    // Optional: different base segment per role if your backend supports it
    const entitySegment =
        normalizedRole === "scout"
            ? "scout"
            : normalizedRole === "club"
                ? "club"
                : "agent"; // default




    // ✅ Reusable fetch so we can refresh after returning from Stripe
    const refreshContracts = useCallback(async () => {
        if (!effectiveId) return;

        try {
            const res = await apiFetch(`/api/contracts/${entitySegment}/${effectiveId}`);
            if (!res.ok) throw new Error(await res.text());
            const all = (await res.json()) || [];

            setContracts(
                all.sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt))
            );
        } catch (err) {
            console.error("Error fetching contracts:", err);
            setError("Failed to fetch contracts.");
        }
    }, [effectiveId, entitySegment]);




    // 1. Ensure we have an id
    useEffect(() => {
        if (!effectiveId) {
            setError("User ID not found. Please log in again.");
            setLoading(false);
        }
    }, [effectiveId]);





    // 2. Fetch contracts after effectiveId is set
    useEffect(() => {
        if (!effectiveId) return;

        setLoading(true);
        refreshContracts().finally(() => setLoading(false));
    }, [effectiveId, refreshContracts]);


    // ✅ Refresh contracts after returning from Stripe (tab focus)
    useEffect(() => {
        if (!effectiveId) return;

        const onFocus = () => {
            refreshContracts();
        };

        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
    }, [effectiveId, refreshContracts]);




    // 3. Fetch termination requests *after* contracts are loaded
    useEffect(() => {
        if (!effectiveId || contracts.length === 0) return;

        const fetchTerminationRequests = async () => {
            try {
                const qs = new URLSearchParams({
                    ownerId: contracts[0]?.playerId,
                    ownerRole: "player",
                }).toString();

                const res = await apiFetch(`/api/termination-requests/owner?${qs}`);
                if (!res.ok) throw new Error(await res.text());
                const allRequests = await res.json();

                // Filter requests based on OWNERSHIP, not requestedBy
                const relevantRequests = allRequests.filter((req) =>
                    contracts.some((c) => c.id === req.contractId)
                );

                // Only contracts that are still active
                const activeContracts = contracts.filter(isContractActive).map((c) => c.id);

                const filtered = relevantRequests
                    .filter((req) => activeContracts.includes(req.contractId))
                    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

                setTerminationRequests(filtered);
            } catch (err) {
                console.error("Error fetching termination requests:", err);
            }
        };

        fetchTerminationRequests();
    }, [contracts, effectiveId]);

    // 4. Contract countdown timers (per contract)
    // - If active (paid): show time left on contract (based on paidAt/activatedAt)
    // - If approved+signed but unpaid: show time left until hold expires (based on activationHoldExpiresAt)
    useEffect(() => {
        if (contracts.length === 0) return;

        const calculateTimers = () => {
            const updatedTimers = {};

            contracts.forEach((contract) => {
                const adminApproved = (contract.adminApproval || "").toLowerCase() === "approved";
                const playerAccepted = !!contract.signedByPlayer;
                const paid = isPaid(contract);

                // Admin not approved yet
                if (!adminApproved) {
                    updatedTimers[contract.id] = "PENDING";
                    return;
                }

                // Approved but player hasn't signed
                if (!playerAccepted) {
                    updatedTimers[contract.id] = "AWAITING_PLAYER";
                    return;
                }

                // Approved + signed but NOT paid yet:
                if (!paid) {
                    const holdExpiresAtMs = getActivationHoldExpiresAtMs(contract);

                    // If backend provides expiry timestamp, show countdown (or expired)
                    if (holdExpiresAtMs) {
                        const msLeft = holdExpiresAtMs - Date.now();
                        updatedTimers[contract.id] = msLeft > 0 ? formatTime(msLeft) : "Expired";
                    } else {
                        // fallback: no expiry timestamp provided, so we can only show awaiting activation
                        updatedTimers[contract.id] = "AWAITING_ACTIVATION";
                    }
                    return;
                }

                // Paid — use activation start time for contract duration countdown
                const startTime = getActivationStartTimeMs(contract);
                if (!startTime) {
                    updatedTimers[contract.id] = "AWAITING_ACTIVATION";
                    return;
                }

                const duration = parseDuration(contract.contractDuration);
                const endTime = startTime + duration;

                const timeLeft = endTime - Date.now();
                updatedTimers[contract.id] = formatTime(timeLeft);
            });

            setTimers(updatedTimers);
            setTimersReady(true); // ✅ MARK TIMERS AS READY (prevents flash)
        };

        calculateTimers();
        const interval = setInterval(calculateTimers, 1000);
        return () => clearInterval(interval);
    }, [contracts]);

    const handleTerminationDecision = async (requestId, decision) => {
        if (submittingDecision) return;
        setSubmittingDecision(true);

        try {
            const payload = {
                requestId,
                decision,
                deciderId: effectiveId,
                deciderRole: normalizedRole,
            };

            const resp = await apiFetch("/api/termination-decision", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (!resp.ok) throw new Error(await resp.text());
            const respData = await resp.json();

            // ✅ Refresh termination requests again (owner params)
            const qs = new URLSearchParams({
                ownerId: contracts[0]?.playerId,
                ownerRole: "player",
            }).toString();

            const res = await apiFetch(`/api/termination-requests/owner?${qs}`);

            if (!res.ok) throw new Error(await res.text());
            const all = await res.json();

            const activeContracts = contracts.filter(isContractActive).map((c) => c.id);

            const filtered = (all || [])
                .filter((req) => contracts.some((c) => c.id === req.contractId))
                .filter((req) => activeContracts.includes(req.contractId))
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

            setTerminationRequests(filtered);

            return respData;
        } catch (err) {
            console.error("Error processing decision:", err);
            alert(err?.message || "Error processing request.");
        } finally {
            setSubmittingDecision(false);
        }
    };

    const handleAgentTerminationRequest = async (contractId, playerId, reason) => {
        if (!reason || !reason.trim()) return;

        try {
            // 1) Cooldown check (GET with query params)
            const qs = new URLSearchParams({
                ownerId: effectiveId,
                ownerRole: normalizedRole,
                contractId,
            }).toString();

            const cooldownRes = await apiFetch(`/api/termination-cooldown?${qs}`);
            if (!cooldownRes.ok) throw new Error(await cooldownRes.text());
            const cooldownData = await cooldownRes.json();

            setCooldownMap((prev) => ({
                ...prev,
                [contractId]: cooldownData,
            }));

            if (!cooldownData.canRequest) {
                alert(cooldownData.message || "You cannot request termination at this time.");
                return;
            }

            // 2) Submit termination request (POST)
            const payload = {
                contractId,
                playerId,
                reason,
                requestedBy: normalizedRole, // 'agent' | 'scout' | 'club'
                ownerId: playerId, // OWNER is always player
                ownerRole: "player",
            };

            const postRes = await apiFetch("/api/request-contract-termination", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (!postRes.ok) throw new Error(await postRes.text());

            // 3) Refresh termination requests for this player (GET with query params)
            const qs2 = new URLSearchParams({
                ownerId: playerId,
                ownerRole: "player",
            }).toString();

            const res = await apiFetch(`/api/termination-requests/owner?${qs2}`);
            if (!res.ok) throw new Error(await res.text());
            const data = await res.json();

            setTerminationRequests((data || []).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
        } catch (err) {
            console.error("Error submitting termination:", err);
            alert(err?.message || "Failed to submit termination request.");
        }
    };

    // ✅ Activation (pay after admin approved + player accepted)
    const activateContract = async (contractId) => {
        if (!contractId) return;

        const ok = window.confirm("Activate this contract now? You will be redirected to payment.");
        if (!ok) return;

        setActivatingMap((prev) => ({ ...prev, [contractId]: true }));
        try {
            const res = await apiFetch(`/api/checkout/offer-to-sign/${contractId}`, {
                method: "POST",
            });

            const data = await res.json().catch(() => ({}));

            if (!res.ok) {
                throw new Error(data?.error || "Failed to start activation checkout.");
            }

            if (!data?.url) {
                throw new Error("Stripe Checkout URL missing from server response.");
            }

            window.location.href = data.url;
        } catch (e) {
            console.error("Activation failed:", e);
            alert(e.message || "Activation failed.");
        } finally {
            setActivatingMap((prev) => ({ ...prev, [contractId]: false }));
        }
    };

    const titleByRole = {
        agent: "Manage Contracts (Agent)",
        scout: "Manage Contracts (Scout)",
        club: "Manage Contracts (Club)",
    };
    const pageTitle = titleByRole[normalizedRole] || "Manage Contracts";

    return (
        <div className="manage-contract-page">
            <div className="manage-contract-container">
                <h2 className="manage-contract-title">{pageTitle}</h2>

                <div className="toggle-tabs">
                    <div
                        className={`toggle-option ${viewMode === "contracts" ? "active" : ""}`}
                        onClick={() => setViewMode("contracts")}
                    >
                        Contracts
                    </div>

                    <div
                        className={`toggle-option ${viewMode === "terminations" ? "active" : ""}`}
                        onClick={() => setViewMode("terminations")}
                        style={{ position: "relative" }}
                    >
                        Termination Requests
                        {pendingTerminationCount > 0 && (
                            <span
                                style={{
                                    position: "absolute",
                                    top: "2px",
                                    right: "6px",
                                    backgroundColor: "red",
                                    color: "white",
                                    borderRadius: "50%",
                                    padding: "2px 6px",
                                    fontSize: "12px",
                                    fontWeight: "bold",
                                    lineHeight: 1,
                                    zIndex: 2,
                                }}
                            >
                                {pendingTerminationCount}
                            </span>
                        )}
                    </div>
                </div>

                {loading ? (
                    <p className="loading">Loading...</p>
                ) : error ? (
                    <p className="error">{error}</p>
                ) : viewMode === "contracts" ? (
                    visibleContracts.length === 0 ? (
                        <div className="manage-contract-content">
                            <p className="manage-contract-empty">No contracts signed or reviewed yet.</p>
                        </div>
                    ) : (
                        visibleContracts.map((contract) => {
                            const relatedRequest = terminationRequests.find(
                                (req) =>
                                    req.contractId === contract.id &&
                                    String(req.requestedBy || "").toLowerCase() === "player"
                            );
                            const active = isContractActive(contract);

                            const adminApproved = (contract.adminApproval || "").toLowerCase() === "approved";
                            const playerAccepted = !!contract.signedByPlayer;
                            const paid = (contract.paymentStatus || "").toLowerCase() === "paid";

                            const holdExpired = isActivationHoldExpired(contract);
                            const hasHoldDeadline = !!getActivationHoldExpiresAtMs(contract);

                            // ✅ Eligible to activate when:
                            // - player accepted
                            // - admin approved
                            // - NOT paid yet
                            // - hold NOT expired (if deadline exists)
                            const canActivate =
                                playerAccepted &&
                                adminApproved &&
                                !paid &&
                                (!hasHoldDeadline || !holdExpired);

                            const activating = !!activatingMap[contract.id];

                            return (
                                <div className="contract-card neon-device" key={contract.id} style={{ position: "relative" }}>
                                    <span className="neon-corner tl" />
                                    <span className="neon-corner tr" />
                                    <span className="neon-corner bl" />
                                    <span className="neon-corner br" />

                                    <div className="neon-screen">
                                        <div className="neon-title-row">
                                            <div className="neon-line" />
                                            <h3 className="neon-title">CONTRACT</h3>
                                            <div className="neon-line" />
                                        </div>

                                        <div className="contract-layout-row">
                                            <div className="contract-left">
                                                <p className="contract-row">
                                                    <span className="contract-label">
                                                        {partnerLabelByRole[normalizedRole] || "Partner Name"}:
                                                    </span>
                                                    <span className="contract-value">
                                                        {partnerNameByRole[normalizedRole]?.(contract) || "Unknown"}
                                                    </span>
                                                </p>

                                                <p className="contract-row">
                                                    <span className="contract-label">Player Name:</span>
                                                    <span className="contract-value">{contract.playerName}</span>
                                                </p>

                                                <p className="contract-row">
                                                    <span className="contract-label">Player Username:</span>
                                                    <span className="contract-value">{contract.playerUsername}</span>
                                                </p>

                                                <p className="contract-row">
                                                    <span className="contract-label">Contract Duration:</span>
                                                    <span className="contract-value">{contract.contractDuration}</span>
                                                </p>

                                                <p className="contract-row">
                                                    <span className="contract-label">View Uploaded Contract:</span>
                                                    <span className="contract-value">
                                                        {contract.agentContractUrl ? (
                                                            <a
                                                                href={toAbsoluteUrl(contract.agentContractUrl)}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                View Contract
                                                            </a>
                                                        ) : (
                                                            "Not uploaded"
                                                        )}
                                                    </span>
                                                </p>

                                                <p className="contract-row">
                                                    <span className="contract-label">Contract With:</span>
                                                    <span className="contract-value">
                                                        {(() => {
                                                            const playerSlug = getPlayerSlug(contract);
                                                            const roleSeg = (normalizedRole || "agent").toLowerCase();

                                                            const href = playerSlug
                                                                ? `${window.location.origin}/${roleSeg}-dashboard/player-profile/${playerSlug}`
                                                                : `${window.location.origin}/${roleSeg}-dashboard/player-profile/${contract.playerId}`;

                                                            return (
                                                                <a href={href} target="_blank" rel="noopener noreferrer">
                                                                    View Player Profile
                                                                </a>
                                                            );
                                                        })()}
                                                    </span>
                                                </p>

                                                <p className="contract-row">
                                                    <span className="contract-label">Status:</span>
                                                    <span className="contract-value">
                                                        {contract.status === "terminated" ? (
                                                            <span className="status-red">❌ Contract Terminated</span>
                                                        ) : contract.signedByPlayer ? (
                                                            <span className="status-green">
                                                                Accepted by Player <span className="tick">✅</span>
                                                            </span>
                                                        ) : (
                                                            <span className="status-pending">⏳ Awaiting Player Signature</span>
                                                        )}
                                                    </span>
                                                </p>

                                                <p className="contract-row">
                                                    <span className="contract-label">Admin Approval:</span>
                                                    <span className="contract-value">
                                                        {(contract.adminApproval || "").toLowerCase() === "approved" ? (
                                                            <span className="status-green">
                                                                Approved <span className="tick">✅</span>
                                                            </span>
                                                        ) : (contract.adminApproval || "").toLowerCase() === "rejected" ? (
                                                            <span className="status-red">❌ Rejected</span>
                                                        ) : (contract.adminApproval || "").toLowerCase() === "closed" ? (
                                                            <span className="status-pending">⏸️ On Hold</span>
                                                        ) : (
                                                            <span className="status-pending">⏳ Pending</span>
                                                        )}
                                                    </span>
                                                </p>

                                                <p className="contract-row">
                                                    <span className="contract-label">Activation / Payment:</span>
                                                    <span className="contract-value">
                                                        {paid ? (
                                                            <span className="status-green">
                                                                Paid <span className="tick">✅</span>
                                                            </span>
                                                        ) : (contract.paymentStatus || "").toLowerCase() === "pendingpayment" ? (
                                                            <span className="status-pending">⏳ Pending Payment</span>
                                                        ) : (
                                                            <span className="status-pending">Unpaid</span>
                                                        )}
                                                    </span>
                                                </p>

                                                <p className="contract-row">
                                                    <span className="contract-label">Signed On:</span>
                                                    <span className="contract-value">
                                                        {new Date(contract.signedOn || contract.submittedAt).toLocaleDateString()}
                                                    </span>
                                                </p>

                                                {relatedRequest && String(relatedRequest.status || "").toLowerCase() !== "rejected" && (
                                                    <div className="termination-request" style={{ marginTop: "10px" }}>
                                                        <p className="contract-row">
                                                            <span className="contract-label">Termination Reason:</span>
                                                            <span className="contract-value">{relatedRequest.reason}</span>
                                                        </p>

                                                        <p className="contract-row">
                                                            <span className="contract-label">Status:</span>
                                                            <span className="contract-value">{relatedRequest.status}</span>
                                                        </p>

                                                        <p className="contract-row">
                                                            <span className="contract-label">Requested At:</span>
                                                            <span className="contract-value">
                                                                {new Date(relatedRequest.createdAt).toLocaleString()}
                                                            </span>
                                                        </p>
                                                    </div>
                                                )}

                                                {/* Optional: show hold deadline for debugging/clarity */}
                                                {contract.activationHoldExpiresAt && !paid && (
                                                    <p className="contract-row">
                                                        <span className="contract-label">Activation Deadline:</span>
                                                        <span className="contract-value">
                                                            {new Date(contract.activationHoldExpiresAt).toLocaleString()}
                                                        </span>
                                                    </p>
                                                )}

                                                {contract.status === "rejected" && contract.playerNote && (
                                                    <p className="contract-row note">
                                                        <span className="contract-label">Player Feedback:</span>
                                                        <span className="contract-value">{contract.playerNote}</span>
                                                    </p>
                                                )}
                                            </div>

                                            {/* Right panel */}
                                            <div className="contract-timer-section neon-right-panel">
                                                    {/* ✅ Activation button (only if still eligible) */}
                                                    {canActivate && (
                                                        <button
                                                            className="activate-btn neon-btn"
                                                            disabled={activating}
                                                            onClick={() => activateContract(contract.id)}
                                                            style={{
                                                                marginBottom: "12px",
                                                                opacity: activating ? 0.6 : 1,
                                                                cursor: activating ? "not-allowed" : "pointer",
                                                            }}
                                                            title="Activate contract by completing payment"
                                                        >
                                                            {activating ? "Redirecting to payment..." : "⚡ Activate Contract"}
                                                        </button>
                                                    )}

                                                {/* ✅ Status / countdown logic (FIXED: distinguish TEMP HOLD vs real Activation Failed) */}
                                                {(() => {
                                                    const status = (contract.status || "").toLowerCase();
                                                    const adminApproval = (contract.adminApproval || "").toLowerCase();
                                                    const tempHold = isTempHoldCompetingOffer(contract);

                                                    // 1) Hard override states FIRST (these must win)
                                                    if (status === "terminated") {
                                                        return null;
                                                    }

                                                    // ✅ IMPORTANT FIX:
                                                    // Competing offers parked while another offer awaits payment should NOT show Activation Failed.
                                                    // They look like:
                                                    //   status=closed, adminApproval=closed, rejectionReason="TEMP_HOLD_AWAITING_PAYMENT:<id>"
                                                    if (status === "closed" && tempHold) {
                                                        return (
                                                            <ContractStatusStamp
                                                                type="pending"
                                                                title="On Hold"
                                                                subtitle="Another offer was approved and is awaiting activation/payment."
                                                            />
                                                        );
                                                    }

                                                    if (status === "closed") {
                                                        if (isClosedBecauseAnotherActivated(contract)) {
                                                            return (
                                                                <ContractStatusStamp
                                                                    type="danger"
                                                                    title="Closed"
                                                                    subtitle="This offer was closed because another contract for this player was successfully activated."
                                                                />
                                                            );
                                                        }

                                                        // ✅ Player rejected (never signed) — but NOT TEMP HOLD (handled above)
                                                        if (isClosedByPlayerRejection(contract)) {
                                                            return (
                                                                <ContractStatusStamp
                                                                    type="danger"
                                                                    title="Rejected by Player"
                                                                    subtitle={
                                                                        contract.playerNote
                                                                            ? `Reason: ${contract.playerNote}`
                                                                            : "Player rejected this offer."
                                                                    }
                                                                />
                                                            );
                                                        }

                                                        // ✅ Otherwise, closed + unpaid => activation window missed / activation failure
                                                        if (!paid) {
                                                            return (
                                                                <ContractStatusStamp
                                                                    type="danger"
                                                                    title="Activation Failed"
                                                                    subtitle="Payment was not completed before the activation window ended."
                                                                />
                                                            );
                                                        }

                                                        // fallback
                                                        return (
                                                            <ContractStatusStamp
                                                                type="danger"
                                                                title="Closed"
                                                                subtitle="This contract is closed."
                                                            />
                                                        );
                                                    }

                                                    if (status === "rejected") {
                                                        return (
                                                            <ContractStatusStamp
                                                                type="danger"
                                                                title="Rejected"
                                                                subtitle="This contract was rejected."
                                                            />
                                                        );
                                                    }

                                                    if (status === "withdrawn") {
                                                        return (
                                                            <ContractStatusStamp
                                                                type="danger"
                                                                title="Withdrawn"
                                                                subtitle="This contract was withdrawn."
                                                            />
                                                        );
                                                    }

                                                    // 2) Normal flow states
                                                    if (!contract.signedByPlayer) {
                                                        return (
                                                            <ContractStatusStamp
                                                                type="pending"
                                                                title="Awaiting Player"
                                                                subtitle="Player has not signed yet"
                                                            />
                                                        );
                                                    }

                                                    // ✅ If adminApproval is "closed" (held) but status isn't closed, show hold clearly
                                                    if (adminApproval === "closed") {
                                                        return (
                                                            <ContractStatusStamp
                                                                type="pending"
                                                                title="On Hold"
                                                                subtitle="This offer is temporarily held while another approved offer attempts activation."
                                                            />
                                                        );
                                                    }

                                                    if (adminApproval !== "approved") {
                                                        return (
                                                            <ContractStatusStamp
                                                                type="pending"
                                                                title="Pending Admin Approval"
                                                                subtitle="Admin review in progress"
                                                            />
                                                        );
                                                    }

                                                    // 3) Approved + signed but unpaid -> activation window countdown (correct)
                                                    if (!paid) {
                                                        if (holdExpired) {
                                                            return (
                                                                <ContractStatusStamp
                                                                    type="danger"
                                                                    title="Activation Failed"
                                                                    subtitle="Payment was not completed before the activation window ended."
                                                                />
                                                            );
                                                        }

                                                        if (hasHoldDeadline) {
                                                            return (
                                                                <>
                                                                    <p className="contract-countdown-message">Activation window expires in:</p>
                                                                    <div className="contract-countdown-timer">
                                                                        {timersReady ? timers[contract.id] : "Loading..."}
                                                                    </div>
                                                                </>
                                                            );
                                                        }

                                                        return (
                                                            <ContractStatusStamp
                                                                type="pending"
                                                                title="Awaiting Activation"
                                                                subtitle="Complete payment to start countdown"
                                                            />
                                                        );
                                                    }

                                                    // 4) Paid -> contract duration countdown
                                                    if (!timersReady) {
                                                        return (
                                                            <ContractStatusStamp
                                                                type="pending"
                                                                title="Loading"
                                                                subtitle="Preparing contract status..."
                                                            />
                                                        );
                                                    }

                                                    if (active) {
                                                        return (
                                                            <>
                                                                <p className="contract-countdown-message">
                                                                    You are currently under contract which expires in:
                                                                </p>
                                                                <div className="contract-countdown-timer">{timers[contract.id]}</div>
                                                            </>
                                                        );
                                                    }

                                                    return <div className="contract-countdown-timer expired">Expired</div>;
                                                })()}

                                                    {/* Only allow termination request while actively under contract and not already expired */}
                                                    {paid && active && timers[contract.id] !== "Expired" && (
                                                        <>
                                                            <button
                                                                className="terminate-button neon-btn danger"
                                                                disabled={
                                                                    relatedRequest?.status === "pending" ||
                                                                    (cooldownMap[contract.id] && cooldownMap[contract.id].loading)
                                                                }
                                                                style={{
                                                                    opacity:
                                                                        relatedRequest?.status === "pending" ||
                                                                            (cooldownMap[contract.id] && cooldownMap[contract.id].loading)
                                                                            ? 0.5
                                                                            : 1,
                                                                    cursor:
                                                                        relatedRequest?.status === "pending" ||
                                                                            (cooldownMap[contract.id] && cooldownMap[contract.id].loading)
                                                                            ? "not-allowed"
                                                                            : "pointer",
                                                                }}
                                                                onClick={async () => {
                                                                    if (relatedRequest?.status === "pending") return;

                                                                    setCooldownMap((prev) => ({
                                                                        ...prev,
                                                                        [contract.id]: {
                                                                            ...(prev[contract.id] || {}),
                                                                            loading: true,
                                                                        },
                                                                    }));

                                                                    try {
                                                                        const qs = new URLSearchParams({
                                                                            ownerId: effectiveId,
                                                                            ownerRole: normalizedRole,
                                                                            contractId: contract.id,
                                                                        }).toString();

                                                                        const cooldownRes = await apiFetch(`/api/termination-cooldown?${qs}`);
                                                                        if (!cooldownRes.ok) throw new Error(await cooldownRes.text());
                                                                        const cooldownData = await cooldownRes.json();

                                                                        setCooldownMap((prev) => ({
                                                                            ...prev,
                                                                            [contract.id]: {
                                                                                ...(prev[contract.id] || {}),
                                                                                ...cooldownData,
                                                                                loading: false,
                                                                            },
                                                                        }));

                                                                        if (!cooldownData.canRequest) {
                                                                            alert(cooldownData.message || "You cannot request termination at this time.");
                                                                            return;
                                                                        }

                                                                        setSelectedContractId(contract.id);
                                                                        setSelectedPlayerId(contract.playerId);
                                                                        setShowReasonModal(true);
                                                                    } catch (err) {
                                                                        console.error("Cooldown check failed:", err);

                                                                        setCooldownMap((prev) => ({
                                                                            ...prev,
                                                                            [contract.id]: {
                                                                                ...(prev[contract.id] || {}),
                                                                                loading: false,
                                                                            },
                                                                        }));

                                                                        alert(err?.message || "Could not verify cooldown status. Please try again.");
                                                                    }
                                                                }}
                                                            >
                                                                {relatedRequest?.status === "pending" ? "Request Sent" : "Request Termination"}
                                                            </button>

                                                            {cooldownMap[contract.id] && !cooldownMap[contract.id].canRequest && (
                                                                <p
                                                                    style={{
                                                                        color: "#ffcc00",
                                                                        marginTop: "10px",
                                                                        fontSize: "14px",
                                                                    }}
                                                                >
                                                                    ⏳ {cooldownMap[contract.id].message}
                                                                </p>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </div>

                                        {contract.status === "terminated" && (
                                            <img
                                                src="/terminated-stamp.png"
                                                alt="Terminated Stamp"
                                                style={{
                                                    position: "absolute",
                                                    top: "50%",
                                                    right: "50px",
                                                    width: "200px",
                                                    opacity: 1,
                                                    transform: "translateY(-50%) rotate(-15deg)",
                                                    zIndex: 10,
                                                    pointerEvents: "none",
                                                }}
                                            />
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )
                ) : terminationRequests.length === 0 ? (
                    <p className="no-contracts">No termination requests found.</p>
                ) : (
                    terminationRequests
                        .filter((req) => contracts.some((c) => c.id === req.contractId))
                        .map((req) => {
                            const relatedContract = contracts.find((c) => c.id === req.contractId);
                            const active = isContractActive(relatedContract);

                            return (
                                <div className="contract-card neon-device" key={req.id}>
                                    <span className="neon-corner tl" />
                                    <span className="neon-corner tr" />
                                    <span className="neon-corner bl" />
                                    <span className="neon-corner br" />

                                    <div className="neon-screen">
                                        <div className="neon-title-row">
                                            <div className="neon-line" />
                                            <h3 className="neon-title">TERMINATION REQUEST</h3>
                                            <div className="neon-line" />
                                        </div>

                                        <p className="contract-row">
                                            <span className="contract-label">Player Name:</span>
                                            <span className="contract-value">
                                                {relatedContract?.playerName || "Unknown"}
                                            </span>
                                        </p>

                                        <p className="contract-row">
                                            <span className="contract-label">Player Username:</span>
                                            <span className="contract-value">
                                                {relatedContract?.playerUsername || "Unknown"}
                                            </span>
                                        </p>

                                        <p className="contract-row">
                                            <span className="contract-label">Player Profile:</span>
                                            <span className="contract-value">
                                                {(() => {
                                                    const playerSlug = getPlayerSlug(relatedContract);
                                                    const roleSeg = (normalizedRole || "agent").toLowerCase();

                                                    const href = playerSlug
                                                        ? `${window.location.origin}/${roleSeg}-dashboard/player-profile/${playerSlug}`
                                                        : `${window.location.origin}/${roleSeg}-dashboard/player-profile/${req.playerId}`;

                                                    return (
                                                        <a href={href} target="_blank" rel="noopener noreferrer">
                                                            View Profile
                                                        </a>
                                                    );
                                                })()}
                                            </span>
                                        </p>

                                        <p className="contract-row">
                                            <span className="contract-label">Reason:</span>
                                            <span className="contract-value">{req.reason}</span>
                                        </p>

                                        <p className="contract-row">
                                            <span className="contract-label">Status:</span>
                                            <span className="contract-value">{req.status}</span>
                                        </p>

                                        <p className="contract-row">
                                            <span className="contract-label">Requested At:</span>
                                            <span className="contract-value">
                                                {new Date(req.createdAt).toLocaleString()}
                                            </span>
                                        </p>

                                        {(req.status || "").toLowerCase() === "pending" &&
                                            (req.requestedBy || "").toLowerCase() === "player" &&
                                            active ? (
                                            <div className="termination-actions">
                                                <button
                                                    className="neon-btn"
                                                    disabled={submittingDecision}
                                                    onClick={() => handleTerminationDecision(req.id, "approved")}
                                                    style={{
                                                        opacity: submittingDecision ? 0.6 : 1,
                                                        cursor: submittingDecision ? "not-allowed" : "pointer",
                                                    }}
                                                >
                                                    {submittingDecision ? "Processing..." : "Approve"}
                                                </button>
                                                <button
                                                    className="neon-btn danger"
                                                    disabled={submittingDecision}
                                                    onClick={() => handleTerminationDecision(req.id, "rejected")}
                                                    style={{
                                                        opacity: submittingDecision ? 0.6 : 1,
                                                        cursor: submittingDecision ? "not-allowed" : "pointer",
                                                    }}
                                                >
                                                    {submittingDecision ? "Processing..." : "Reject"}
                                                </button>
                                            </div>
                                        ) : !active ? (
                                            <p
                                                style={{
                                                    color: "orange",
                                                    fontWeight: "bold",
                                                }}
                                            >
                                                ⏳ Contract is no longer active — no action available
                                            </p>
                                        ) : null}
                                    </div>
                                </div>
                            );
                        })
                )}
            </div>

            {showReasonModal && (
                <div className="termination-modal">
                    <div className="modal-content">
                        <h3>Enter Termination Reason</h3>
                        <textarea
                            value={terminationReason}
                            onChange={(e) => setTerminationReason(e.target.value)}
                            placeholder="Write reason for requesting termination..."
                            rows={5}
                        />
                        <div
                            style={{
                                marginTop: "10px",
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: "10px",
                            }}
                        >
                            <button
                                className="neon-btn"
                                disabled={submittingRequest || !terminationReason.trim()}
                                onClick={async () => {
                                    if (submittingRequest) return;
                                    setSubmittingRequest(true);
                                    try {
                                        await handleAgentTerminationRequest(
                                            selectedContractId,
                                            selectedPlayerId,
                                            terminationReason
                                        );
                                        setShowReasonModal(false);
                                        setTerminationReason("");
                                    } catch (err) {
                                        console.warn("Submission failed, keeping modal open for retry.");
                                    } finally {
                                        setSubmittingRequest(false);
                                    }
                                }}
                                style={{
                                    opacity: submittingRequest ? 0.6 : 1,
                                    cursor: submittingRequest ? "not-allowed" : "pointer",
                                }}
                            >
                                {submittingRequest ? "Submitting..." : "Submit"}
                            </button>

                            <button className="neon-btn" onClick={() => setShowReasonModal(false)}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageContract;

